/**
 * @Author: Klaudie <klaudie@foxprofi.cz>
 */

import React, { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { toast } from 'sonner'
import { getRoles } from '@/api/users/users.api'
import { createUser, updateUser } from '@/api/users/users.api'
import type { UserProfile } from '@/types/auth.types'
import type { CreateUserRequest, UpdateUserRequest } from '@/api/users/users.api'

interface UserEditDialogProps {
  user: UserProfile | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess: () => void
}

export function UserEditDialog({
  user,
  open,
  onOpenChange,
  onSuccess,
}: UserEditDialogProps) {
  const isCreating = !user
  const [formData, setFormData] = useState<CreateUserRequest | UpdateUserRequest>({
    email: '',
    firstName: '',
    lastName: '',
    phone: '',
    avatar: '',
    isActive: true,
    roleIds: [],
    password: '',
  })
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Načtení rolí
  const { data: roles } = useQuery({
    queryKey: ['roles'],
    queryFn: getRoles,
  })

  // Inicializace formuláře při otevření dialogu
  useEffect(() => {
    if (user) {
      setFormData({
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
        phone: user.phone || '',
        avatar: user.avatar || '',
        isActive: user.isActive,
        roleIds: user.roles,
      })
    } else {
      // Reset pro vytvoření nového uživatele
      setFormData({
        email: '',
        firstName: '',
        lastName: '',
        phone: '',
        avatar: '',
        isActive: true,
        roleIds: [],
        password: '',
      })
    }
  }, [user, open])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.email || !formData.firstName || !formData.lastName) {
      toast.error('Vyplňte povinná pole')
      return
    }

    if (isCreating && !(formData as CreateUserRequest).password) {
      toast.error('Heslo je povinné pro nového uživatele')
      return
    }

    if (formData.roleIds && formData.roleIds.length === 0) {
      toast.error('Vyberte alespoň jednu roli')
      return
    }

    setIsSubmitting(true)
    try {
      if (isCreating) {
        await createUser(formData as CreateUserRequest)
        toast.success('Uživatel byl úspěšně vytvořen')
      } else {
        await updateUser(user.id, formData as UpdateUserRequest)
        toast.success('Uživatel byl úspěšně aktualizován')
      }
      onSuccess()
      onOpenChange(false)
    } catch (error: any) {
      console.error('Error saving user:', error)
      const message = error.response?.data?.message || 'Chyba při ukládání uživatele'
      toast.error(message)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleChange = (field: keyof typeof formData, value: string | boolean | string[]) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  const toggleRole = (roleId: string) => {
    const current = formData.roleIds || []
    if (current.includes(roleId)) {
      handleChange('roleIds', current.filter((id) => id !== roleId))
    } else {
      handleChange('roleIds', [...current, roleId])
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isCreating ? 'Vytvořit uživatele' : 'Upravit uživatele'}</DialogTitle>
          <DialogDescription>
            {isCreating
              ? 'Vyplňte informace o novém uživateli'
              : 'Upravte údaje uživatele. Po uložení se změny projeví v seznamu.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            {/* Email */}
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="email" className="text-right">
                Email *
              </Label>
              <Input
                id="email"
                type="email"
                value={formData.email || ''}
                onChange={(e) => handleChange('email', e.target.value)}
                className="col-span-3"
                required
              />
            </div>

            {/* Heslo (pouze pro vytvoření) */}
            {isCreating && (
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="password" className="text-right">
                  Heslo *
                </Label>
                <Input
                  id="password"
                  type="password"
                  value={(formData as CreateUserRequest).password || ''}
                  onChange={(e) => handleChange('password', e.target.value)}
                  className="col-span-3"
                  required
                  minLength={8}
                />
              </div>
            )}

            {/* Jméno */}
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="firstName" className="text-right">
                Jméno *
              </Label>
              <Input
                id="firstName"
                value={formData.firstName || ''}
                onChange={(e) => handleChange('firstName', e.target.value)}
                className="col-span-3"
                required
              />
            </div>

            {/* Příjmení */}
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="lastName" className="text-right">
                Příjmení *
              </Label>
              <Input
                id="lastName"
                value={formData.lastName || ''}
                onChange={(e) => handleChange('lastName', e.target.value)}
                className="col-span-3"
                required
              />
            </div>

            {/* Telefon */}
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="phone" className="text-right">
                Telefon
              </Label>
              <Input
                id="phone"
                type="tel"
                value={formData.phone || ''}
                onChange={(e) => handleChange('phone', e.target.value)}
                className="col-span-3"
              />
            </div>

            {/* Avatar URL */}
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="avatar" className="text-right">
                Avatar URL
              </Label>
              <Input
                id="avatar"
                type="url"
                value={formData.avatar || ''}
                onChange={(e) => handleChange('avatar', e.target.value)}
                className="col-span-3"
              />
            </div>

            {/* Aktivní */}
            <div className="grid grid-cols-4 items-center gap-4">
              <Label className="text-right">Stav</Label>
              <div className="col-span-3 flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={formData.isActive}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('isActive', e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300"
                />
                <Label htmlFor="isActive" className="cursor-pointer">
                  Aktivní uživatel
                </Label>
              </div>
            </div>

            {/* Role */}
            <div className="grid grid-cols-4 items-start gap-4">
              <Label className="text-right pt-2">Role *</Label>
              <div className="col-span-3 space-y-2 border rounded-md p-4">
                {roles?.map((role) => (
                  <div key={role.id} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id={`role-${role.id}`}
                      checked={formData.roleIds?.includes(role.id) || false}
                      onChange={() => toggleRole(role.id)}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                    <Label htmlFor={`role-${role.id}`} className="cursor-pointer">
                      {role.name}
                      {role.description && (
                        <span className="text-sm text-muted-foreground ml-2">
                          ({role.description})
                        </span>
                      )}
                    </Label>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Zrušit
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Ukládám...' : isCreating ? 'Vytvořit' : 'Uložit změny'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
